<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import {
  isPinnedPhone,
  isPinnedBranchCode,
  PINNED_DISPLAY_NAME,
  PINNED_WARNING,
} from 'dashboard/helper/pinnedContact';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './MessagePreview.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import PriorityMark from './PriorityMark.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import NewTaskModal from './NewTaskModal.vue';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
  // Fijar chat estilo WhatsApp (Victor 2026-06-02): true si el usuario lo fijó.
  isUserPinned: { type: Boolean, default: false },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'removeLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
  'toggleUserPin',
]);

const router = useRouter();
const store = useStore();

const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });
const showTaskModal = ref(false);

const resetState = () => {
  if (showContextMenu.value) emit('contextMenuToggle', false);
  hovered.value = false;
  showContextMenu.value = false;
  contextMenu.value = { x: null, y: null };
};

watch(() => props.chat.id, resetState);

const currentChat = useMapGetter('getSelectedChat');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');

const chatMetadata = computed(() => props.chat.meta || {});

// Grupos OFICIALES (Victor 2026-06-05): muestran etiqueta dorada "OFICIAL" en la
// lista. Son los únicos grupos que aparecen en "Todos" de la personal.
const OFFICIAL_GROUP_IDS = [
  '120363252339289744@g.us', // Oficial Víctor Guzmán Fotografía
  '120363405751497806@g.us', // Oficial La Romana
  '120363404466875975@g.us', // Oficial Equipo SDE
  '120363399239307709@g.us', // Oficial Santiago
];
const isOfficialGroup = computed(() =>
  OFFICIAL_GROUP_IDS.includes(props.chat?.meta?.sender?.identifier)
);
const assignee = computed(() => chatMetadata.value.assignee || {});

// Vista compartida · 2026-05-18 (Victor): si las sucursales conectadas están
// activas, en la lista "Todos" cada chat muestra un chip de quién lo atiende
// ("Tú" si es mío · nombre del agente si es de otro · "Libre" si nadie lo tomó).
const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](accountId.value)
);
const connectedBranchIds = computed(() => {
  const ids = currentAccount.value?.settings?.connected_branch_ids;
  return Array.isArray(ids) ? ids : [];
});
const sharedView = computed(() => connectedBranchIds.value.length >= 2);
// El chip de presencia SOLO aplica a chats de sucursales conectadas (SDO+SDE).
// Personal Privado y demás sucursales NO llevan chip (regla 2026-05-19 Victor).
const isConnectedConversation = computed(() => {
  const bid = props.chat.branch_id || inbox.value?.branch_id;
  return bid && connectedBranchIds.value.includes(bid);
});
const presenceChip = computed(() => {
  if (!sharedView.value || !isConnectedConversation.value) return null;
  const me = currentUser.value;
  const a = assignee.value;
  if (a?.id && me?.id && a.id === me.id) {
    return { kind: 'me', label: 'Tú' };
  }
  if (a?.id) {
    const name = a.name || 'Otro agente';
    return {
      kind: 'busy',
      label: name,
      initial: (name || '?').trim().charAt(0).toUpperCase(),
    };
  }
  return { kind: 'free', label: 'Libre' };
});
const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() => {
  return senderId.value
    ? store.getters['contacts/getContact'](senderId.value)
    : {};
});

const isActiveChat = computed(() => currentChat.value.id === props.chat.id);
const unreadCount = computed(() => props.chat.unread_count);
const lastMessageInChat = computed(() => getLastMessage(props.chat));
// Propuesta 3 (Victor 2026-06-09): el "no leído" (azul + globo 💬N) aparece SOLO
// cuando el ÚLTIMO mensaje es del cliente (entrante, message_type 0). Si el último
// lo envié yo (desde WhatsApp o el CRM = outgoing/plantilla), la fila queda neutra
// aunque unread_count no se haya reseteado — así no confundo mi mensaje con uno del
// cliente sin leer.
const hasUnread = computed(
  () => unreadCount.value > 0 && lastMessageInChat.value?.message_type === 0
);

// Indicador de tiempo (Victor 2026-05-20 · T4): UNA sola duración desde la
// última actividad del cliente, con color de urgencia. Reemplaza el "4d · 4d".
// Verde <30min · gris <24h · ámbar <7d · rojo >7d.
const nowTs = ref(Date.now());
let waitTimer = null;
onMounted(() => {
  waitTimer = setInterval(() => {
    nowTs.value = Date.now();
  }, 60000);
});
onUnmounted(() => clearInterval(waitTimer));

const waitInfo = computed(() => {
  const ts = Number(props.chat.timestamp || props.chat.created_at || 0);
  if (!ts) return { label: '', level: 'neutral' };
  const diffMs = nowTs.value - ts * 1000;
  const min = Math.floor(diffMs / 60000);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  let label;
  if (min < 1) label = 'ahora';
  else if (min < 60) label = `${min}m`;
  else if (hr < 24) label = `${hr}h`;
  else label = `${day}d`;
  let level;
  if (min < 30) level = 'ok';
  else if (hr < 24) level = 'neutral';
  else if (day < 7) level = 'warn';
  else level = 'danger';
  return { label, level };
});
const waitLabel = computed(() => waitInfo.value.label);
const waitLevel = computed(() => waitInfo.value.level);
const waitTooltip = computed(() => {
  const ts = Number(props.chat.timestamp || 0);
  return ts ? new Date(ts * 1000).toLocaleString('es-DO') : '';
});

const voiceCallData = computed(() => ({
  status: props.chat.additional_attributes?.call_status,
  direction: props.chat.additional_attributes?.call_direction,
}));

const inboxId = computed(() => props.chat.inbox_id);
const inbox = computed(() => {
  const stored = inboxId.value
    ? store.getters['inboxes/getInbox'](inboxId.value)
    : {};
  // Vista compartida (Victor 2026-05-26): si el agente no es miembro del inbox
  // y su sucursal no está en accessible_branch_ids, la policy no lo carga en su
  // store → llega {} → el ícono de red cae a globo genérico. El payload de la
  // conversación trae channel_type (meta.channel) y el nombre (meta.inbox_name),
  // suficiente para que channelCode detecte la red (incl. bridges Channel::Api).
  if (!stored?.id && props.chat?.meta?.channel) {
    return {
      channel_type: props.chat.meta.channel,
      name: props.chat.meta.inbox_name || '',
    };
  }
  return stored;
});

// WA · code corto del canal para acentos de color en la card (bar lateral + counter)
// 'wa' | 'ig' | 'fb' | 'tk' | 'tw' | 'email' | 'web' (default)
// IMPORTANTE: bridges custom (Baileys, TikTok) usan `Channel::Api` · detectamos
// por nombre del inbox cuando el channel_type es genérico.
const channelCode = computed(() => {
  const t = (inbox.value?.channel_type || '').toLowerCase();
  const name = (inbox.value?.name || '').toLowerCase();

  // 1) Detección directa por channel_type (Chatwoot estándar)
  if (t.includes('whatsapp')) return 'wa';
  if (t.includes('instagram')) return 'ig';
  if (t.includes('facebook') || t.includes('messenger')) return 'fb';
  if (t.includes('tiktok')) return 'tk';
  if (t.includes('twitter')) return 'tw';
  if (t.includes('email')) return 'email';
  if (t.includes('sms') || t.includes('twilio')) return 'sms';
  if (t.includes('telegram')) return 'tg';
  if (t.includes('line')) return 'line';

  // 2) Bridges custom (Channel::Api u otros) · detectar por nombre del inbox
  if (t.includes('api') || t === '') {
    if (name.includes('whatsapp') || name.includes('wa ') || name === 'wa') return 'wa';
    if (name.includes('tiktok') || name.includes('tk ')) return 'tk';
    if (name.includes('instagram') || name.includes(' ig ')) return 'ig';
    if (name.includes('messenger') || name.includes('facebook')) return 'fb';
  }

  return 'web';
});
const channelIcon = computed(() => {
  const map = { wa: '💬', ig: '📸', fb: 'f', tk: '♪', tw: '𝕏', email: '✉', sms: '📱', tg: '✈', line: 'L', web: '🌐' };
  return map[channelCode.value] || '🌐';
});

// CV2 · branch info derivada del inbox o conversation.branch_id (multi-sucursal)
const branchList = computed(() => store.getters['branches/getBranches'] || []);
const branchId = computed(() => props.chat.branch_id || inbox.value?.branch_id);
const branch = computed(() => {
  if (!branchId.value) return null;
  return branchList.value.find(b => b.id === branchId.value);
});
const branchClass = computed(() => {
  if (!branch.value) return 'none';
  if (branch.value.is_personal) return 'pers';
  const code = (branch.value.code || '').toLowerCase();
  if (['lrm', 'sde', 'sdo', 'stg'].includes(code)) return code;
  return 'other';
});
const branchShort = computed(() => {
  if (!branch.value) return '';
  return (branch.value.code || branch.value.name?.substring(0, 3) || '').toUpperCase();
});

// Etiqueta de sucursal en resultados de búsqueda y listas (Victor 2026-06-08):
// parado en CUALQUIER sucursal, un resultado de OTRA sucursal lleva un chip de
// color con su código (SDO/ROMANA/…) para saber de dónde es sin cambiar de
// sucursal. Si la conv es de la sucursal activa → sin chip (sería redundante).
// Blindaje personal: la etiqueta de una sucursal personal/owner-only solo se
// renderiza para su dueño (owner_only_user_id); jamás para Victor Manuel/Gabriela.
const activeBranchId = computed(() => store.getters['branches/getActiveBranchId']);
const personalTagAllowed = computed(() => {
  const ownerId = branch.value?.owner_only_user_id;
  if (!ownerId) return true; // no es sucursal owner-only → etiqueta libre
  return currentUser.value?.id === ownerId; // owner-only → solo su dueño
});
const showBranchTag = computed(
  () =>
    !!branchId.value &&
    branchId.value !== activeBranchId.value &&
    !!branchShort.value &&
    personalTagAllowed.value
);
const branchTagClass = computed(() => {
  const map = {
    sdo: 'bg-[#caa15c]/15 text-[#e7cf9a] border-[#caa15c]/40',
    lrm: 'bg-[#cf6a6a]/15 text-[#f0b0b0] border-[#cf6a6a]/40',
    stg: 'bg-[#6a8ccf]/15 text-[#aac4f0] border-[#6a8ccf]/40',
    sde: 'bg-[#6acf9b]/15 text-[#a6e7c8] border-[#6acf9b]/40',
  };
  return map[branchClass.value] || 'bg-white/10 text-n-slate-11 border-white/15';
});

// Número personal de Victor fijado (2026-05-22): nombre fijo + advertencia.
// Solo en sucursales LRM/SDE/SDO/STG (ChatList ya lo pinnea de primero).
const isPinnedConversation = computed(
  () => isPinnedPhone(phoneNumber.value) && isPinnedBranchCode(branchShort.value)
);
const pinnedName = PINNED_DISPLAY_NAME;
const pinnedWarning = PINNED_WARNING;

// Duplicates · otras conversaciones del mismo contacto en otra sucursal
// Solo se muestra el indicador en la conversación MÁS RECIENTE del contacto.
const allConversations = useMapGetter('getAllConversations');
const phoneNumber = computed(() => currentContact.value?.phone_number || chatMetadata.value.sender?.phone_number);

const duplicatesInOtherBranches = computed(() => {
  if (!senderId.value && !phoneNumber.value) return [];
  const me = props.chat;
  return (allConversations.value || []).filter(c => {
    if (c.id === me.id) return false;
    const sameSender = c.meta?.sender?.id === senderId.value;
    const samePhone = phoneNumber.value && c.meta?.sender?.phone_number === phoneNumber.value;
    if (!sameSender && !samePhone) return false;
    const cBranchId = c.branch_id;
    if (!cBranchId || !branchId.value) return false;
    return cBranchId !== branchId.value;
  });
});

const isMostRecentForContact = computed(() => {
  if (duplicatesInOtherBranches.value.length === 0) return true;
  const mostRecentTs = Math.max(
    props.chat.last_activity_at || 0,
    ...duplicatesInOtherBranches.value.map(c => c.last_activity_at || 0)
  );
  return (props.chat.last_activity_at || 0) >= mostRecentTs;
});

const duplicateBadgeInfo = computed(() => {
  if (!isMostRecentForContact.value) return null;
  if (duplicatesInOtherBranches.value.length === 0) return null;
  const mostRecentDup = [...duplicatesInOtherBranches.value]
    .sort((a, b) => (b.last_activity_at || 0) - (a.last_activity_at || 0))[0];
  if (!mostRecentDup) return null;
  const dupBranch = branchList.value.find(b => b.id === mostRecentDup.branch_id);
  if (!dupBranch) return null;
  const dupClass = dupBranch.is_personal ? 'pers'
    : (['lrm', 'sde', 'sdo', 'stg'].includes((dupBranch.code || '').toLowerCase()) ? (dupBranch.code || '').toLowerCase() : 'other');
  return {
    chatId: mostRecentDup.id,
    branchName: dupBranch.name?.toUpperCase() || '',
    branchClass: dupClass,
    count: duplicatesInOtherBranches.value.length,
  };
});

function jumpToDuplicate(e) {
  if (!duplicateBadgeInfo.value) return;
  e.stopPropagation();
  e.preventDefault();
  const path = frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: null,
      id: duplicateBadgeInfo.value.chatId,
      label: '',
      teamId: 0,
      conversationType: '',
      foldersId: 0,
    })
  );
  router.push({ path });
}

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);
const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

const onCardClick = e => {
  // Si acabamos de abrir el menú con "mantener presionado", el toque no abre el chat.
  if (longPressFired) {
    longPressFired = false;
    e?.preventDefault();
    return;
  }
  // Si está deslizado mostrando "Fijar", el primer toque solo lo cierra.
  if (swipeX.value > 0) {
    e?.preventDefault();
    swipeX.value = 0;
    return;
  }
  // Sin restricción al abrir (Victor 2026-06-03): el clic en CUALQUIER tarjeta
  // abre el chat, sin importar quién es el asignado/responsable ni si es urgente.
  // Todos los agentes pueden entrar a cualquier conversación. Los botones
  // "🙋 ATENDER" / "👁 VER" siguen disponibles para acciones explícitas, pero
  // ya NO son obligatorios para abrir. (El backend ya lo permite vía
  // ConversationPolicy#account_member?.)
  const path = conversationPath.value;
  if (!path) return;
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(`${window.chatwootConfig.hostURL}${path}`, '_blank', 'noopener,noreferrer');
    return;
  }
  if (isActiveChat.value) return;
  router.push({ path });
};

const onThumbnailHover = () => { hovered.value = !props.hideThumbnail; };
const onThumbnailLeave = () => { hovered.value = false; };

// === Fijar chat · swipe (celular) + botón al pasar el mouse (PC) ===
// Deslizar de izquierda→derecha revela el botón "Fijar/Desfijar". Por clic, así
// funciona igual en celular, laptop y PC (Victor 2026-06-02).
const swipeX = ref(0);
const swipeAnimate = ref(false); // animar solo al soltar, no durante el arrastre
const SWIPE_REVEAL = 84;
let touchStartX = 0;
let touchStartY = 0;
let touchActive = false;
// === Mantener presionado (celular): abre el menú de opciones igual que el clic
// derecho en PC. Si el dedo se mueve (scroll/swipe) o se suelta antes, se cancela.
const LONGPRESS_MS = 600;
let longPressTimer = null;
let longPressFired = false;
const clearLongPress = () => {
  if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
};
const onPinTouchStart = e => {
  if (!props.enableContextMenu) return;
  if (e.touches?.length !== 1) return;
  touchStartX = e.touches[0].clientX;
  touchStartY = e.touches[0].clientY;
  touchActive = true;
  swipeAnimate.value = false;
  longPressFired = false;
  clearLongPress();
  const px = e.touches[0].pageX || e.touches[0].clientX;
  const py = e.touches[0].pageY || e.touches[0].clientY;
  longPressTimer = setTimeout(() => {
    longPressFired = true;
    touchActive = false; // cancela el swipe en curso
    swipeAnimate.value = true;
    swipeX.value = 0;
    if (navigator.vibrate) navigator.vibrate(15);
    openContextMenuAt(px, py);
  }, LONGPRESS_MS);
};
const onPinTouchMove = e => {
  if (!touchActive) return;
  const dx = e.touches[0].clientX - touchStartX;
  const dy = e.touches[0].clientY - touchStartY;
  // cualquier movimiento perceptible cancela el "mantener presionado"
  if (Math.abs(dx) > 10 || Math.abs(dy) > 10) clearLongPress();
  // si el gesto es más vertical (scroll de la lista), cancelamos el swipe
  if (Math.abs(dy) > Math.abs(dx)) {
    touchActive = false;
    swipeAnimate.value = true;
    swipeX.value = 0;
    return;
  }
  swipeX.value = Math.max(0, Math.min(dx, SWIPE_REVEAL));
};
const onPinTouchEnd = () => {
  clearLongPress();
  if (!touchActive) return;
  touchActive = false;
  swipeAnimate.value = true;
  swipeX.value = swipeX.value > SWIPE_REVEAL / 2 ? SWIPE_REVEAL : 0;
};
const toggleUserPin = () => {
  emit('toggleUserPin', props.chat.id);
  swipeAnimate.value = true;
  swipeX.value = 0;
};

const onSelectConversation = checked => {
  if (checked) emit('selectConversation', props.chat.id, inbox.value.id);
  else emit('deSelectConversation', props.chat.id, inbox.value.id);
};

const openContextMenuAt = (x, y) => {
  if (!props.enableContextMenu) return;
  emit('contextMenuToggle', true);
  contextMenu.value.x = x;
  contextMenu.value.y = y;
  showContextMenu.value = true;
};

const openContextMenu = e => {
  if (!props.enableContextMenu) return;
  e.preventDefault();
  openContextMenuAt(e.pageX || e.clientX, e.pageY || e.clientY);
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};
const onAssignAgent = agent => { emit('assignAgent', agent, [props.chat.id]); closeContextMenu(); };
const onAssignLabel = label => { emit('assignLabel', [label.title], [props.chat.id]); };
const onRemoveLabel = label => { emit('removeLabel', [label.title], [props.chat.id]); };
const onAssignTeam = team => { emit('assignTeam', team, props.chat.id); closeContextMenu(); };
const markAsUnread = () => { emit('markAsUnread', props.chat.id); closeContextMenu(); };
const markAsRead = () => { emit('markAsRead', props.chat.id); closeContextMenu(); };
const assignPriority = priority => { emit('assignPriority', priority, props.chat.id); closeContextMenu(); };
const deleteConversation = () => { emit('deleteConversation', props.chat.id); closeContextMenu(); };
const openTaskModal = () => { showTaskModal.value = true; closeContextMenu(); };
const closeTaskModal = () => { showTaskModal.value = false; };

// Atender (M v3 · M1.B): la conversación NO es mía (otro agente es responsable)
// y está urgente (>20min sin respuesta). El agente disponible la "atiende" sin
// cambiar el assignee · el responsable original sigue siendo el dueño y puede
// reclamar al volver. La conversación SIGUE visible para todos los agentes en el
// tab Urgentes; los terceros pueden ver pero no responder (bloqueo en ReplyBox).
//
// Estados del badge:
//   - 'available'  → nadie atiende · muestro botón ATENDER azul (puedo tomar)
//   - 'attending'  → YO atiendo · muestro badge verde "Estás atendiendo"
//   - 'taken'      → OTRO atiende · muestro badge púrpura "Atendiendo: Nombre"
const URGENT_THRESHOLD_MS = 30 * 60 * 1000; // V2 · 2026-05-14 (Victor): 20→30 min
const ATTENDING_TTL_MS = 30 * 60 * 1000;
const attendInfo = computed(() => {
  const me = currentUser.value;
  if (!me?.id) return null;
  const c = props.chat;
  const assignee = c?.meta?.assignee;
  if (!assignee?.id) return null;          // sin assignee → no aplica
  if (assignee.id === me.id) return null;  // ya es mía
  const lastMsg = c?.last_non_activity_message;
  if (!lastMsg) return null;
  if (lastMsg.message_type !== 0) return null; // 0 = incoming (cliente)
  let tMs = lastMsg.created_at || 0;
  if (!tMs) return null;
  if (tMs < 1e12) tMs *= 1000;
  if (Date.now() - tMs <= URGENT_THRESHOLD_MS) return null;
  // Detectar si hay un atendedor activo (TTL 30 min) · ignorar si fue reclaimed (M v5)
  const attendingId = c?.additional_attributes?.attending_user_id;
  const attendingName = c?.additional_attributes?.attending_user_name;
  const attendingSinceSec = c?.additional_attributes?.attending_since;
  const attendingReclaimedAt = c?.additional_attributes?.attending_reclaimed_at;
  const hasActiveAttender = attendingId && attendingSinceSec && !attendingReclaimedAt &&
    (Date.now() - Number(attendingSinceSec) * 1000 < ATTENDING_TTL_MS);
  const assigneeName = assignee.name || 'Otro agente';
  const assigneeInitial = (assigneeName || '?').trim().charAt(0).toUpperCase();
  if (hasActiveAttender) {
    if (Number(attendingId) === me.id) {
      return { state: 'attending', name: assigneeName, initial: assigneeInitial };
    }
    const attName = attendingName || 'otro agente';
    const attInitial = (attName || '?').trim().charAt(0).toUpperCase();
    return { state: 'taken', attenderName: attName, attenderInitial: attInitial };
  }
  return { state: 'available', name: assigneeName, initial: assigneeInitial };
});

const attending = ref(false);
const onAttend = async e => {
  e?.stopPropagation();
  e?.preventDefault();
  if (attending.value) return;
  attending.value = true;
  try {
    const { data } = await ConversationAPI.take(props.chat.id);
    const prev = data?.previous_assignee_name || attendInfo.value?.name || 'el responsable';
    useAlert(`🙋 Estás atendiendo esta conversación · ${prev} sigue siendo el responsable`);
    // M v4 · navegar al chat automáticamente tras tomar (Victor: "click en
    // atender debe abrirme el chat para no confundirme").
    const path = conversationPath.value;
    if (path && !isActiveChat.value) router.push({ path });
  } catch (err) {
    useAlert('No se pudo tomar la conversación. Intenta de nuevo.');
  } finally {
    attending.value = false;
  }
};

// M v4 · M1.B: terceros que solo quieren VER la conversación (sin atender).
// Modo solo lectura · ReplyBox.vue ya bloquea el input para no-atendedores.
const onView = e => {
  e?.stopPropagation();
  e?.preventDefault();
  const path = conversationPath.value;
  if (!path || isActiveChat.value) return;
  router.push({ path });
};

// Reclaim (J3 · M1.B · v2): la conversación me pertenece como responsable y el último
// mensaje saliente lo envió un humano (yo u otro agente). Retorna:
//   - { state: 'other', name, initial } → OTRO agente respondió · badge dorado + RECLAMAR
//   - { state: 'self' }                  → YO respondí (tarde) · badge gris informativo · sin botón
//   - null                               → no hay último outgoing humano, o no soy responsable
// Comparación con Number() para evitar mismatch string/number entre sender_id y me.id.
const reclaimInfo = computed(() => {
  const me = currentUser.value;
  if (!me?.id) return null;
  const c = props.chat;
  if (c?.meta?.assignee?.id !== me.id) return null;
  // Si ya fue reclamado → no mostrar badge "reclamable" · evita doble reclaim.
  if (c?.additional_attributes?.attending_reclaimed_at) return null;
  const lastMsg = c?.last_non_activity_message;
  if (!lastMsg) return null;
  if (lastMsg.message_type !== 1) return null; // 1 = outgoing
  if (lastMsg.sender_type !== 'User') return null; // descartar bots/sistema
  if (lastMsg.private) return null; // las private notes no cuentan como respuesta al cliente
  const sid = Number(lastMsg.sender?.id ?? lastMsg.sender_id ?? 0);
  const myId = Number(me.id);
  if (!sid) return null;
  if (sid === myId) return { state: 'self' };
  const name = lastMsg.sender?.name || 'Otro agente';
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return { state: 'other', name, initial };
});

const reclaiming = ref(false);
const onReclaim = async e => {
  e?.stopPropagation();
  e?.preventDefault();
  if (reclaiming.value) return;
  reclaiming.value = true;
  try {
    const { data } = await ConversationAPI.reclaim(props.chat.id);
    const other = data?.other_agent_name || reclaimInfo.value?.name || 'el otro agente';
    useAlert(`✋ Reclamado · ${other} fue notificado`);
    // Auto-navegar al chat tras reclaim para que el responsable entre directo
    // y vea la nota privada al ex-atendedor.
    const path = conversationPath.value;
    if (path) router.push({ path });
  } catch (err) {
    useAlert('No se pudo reclamar. Intenta de nuevo.');
  } finally {
    reclaiming.value = false;
  }
};
</script>

<template>
  <!-- WA · ChatList row · estilo WhatsApp Web limpio + N2 unread channel-aware
       (badges/botones ATENDER/RECLAMAR/Asignado movidos al header del chat) -->
  <div class="wa-swipe-wrap">
    <!-- Acción revelada al deslizar → (celular): Fijar / Desfijar -->
    <button
      v-show="swipeX > 0 && enableContextMenu"
      type="button"
      class="wa-swipe-fijar"
      @click.stop="toggleUserPin"
    >
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 3l5 5-1.5 1.5-1-1-4 4 .5 4.5L13 19l-3-3-4 4-1-1 4-4-3-3 1.5-1.5L12 11l4-4-1-1L16 3z" />
      </svg>
      <span>{{ isUserPinned ? 'Desfijar' : 'Fijar' }}</span>
    </button>
    <div
      class="wa-row"
      :class="[
        `wa-ch-${channelCode}`,
        {
          'wa-active': isActiveChat,
          'wa-selected': selected,
          'wa-unread': hasUnread,
          'wa-pinned': isPinnedConversation,
          'wa-userpinned': isUserPinned,
          'wa-swiping': swipeX > 0,
          'wa-swipe-animate': swipeAnimate,
        }
      ]"
      :style="swipeX ? { transform: `translateX(${swipeX}px)` } : null"
      @click="onCardClick"
      @contextmenu.prevent.stop="openContextMenu($event)"
      @touchstart.passive="onPinTouchStart"
      @touchmove.passive="onPinTouchMove"
      @touchend="onPinTouchEnd"
    >
    <div
      class="wa-avatar-wrap"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="44"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="wa-checkbox-overlay"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <input
              :value="selected"
              :checked="selected"
              class="!m-0 cursor-pointer"
              type="checkbox"
              @change="onSelectConversation($event.target.checked)"
            />
          </label>
        </template>
      </Avatar>
      <span class="wa-ch-badge" :class="`wa-ch-badge-${channelCode}`" :title="channelCode.toUpperCase()">
        <!-- WhatsApp iOS-style: phone-in-bubble (logo oficial) -->
        <svg v-if="channelCode === 'wa'" class="wa-ch-svg" viewBox="0 0 32 32">
          <path fill="#fff" d="M27.1 4.9C24.2 1.9 20.2 0.3 16 0.3 7.4 0.3 0.3 7.3 0.3 16c0 2.8 0.7 5.5 2.1 7.9L0.1 31.7l8-2.1c2.3 1.3 4.9 2 7.6 2h0c8.7 0 15.7-7.1 15.7-15.7 0-4.2-1.6-8.1-4.3-11zM16 28.9h0c-2.4 0-4.8-0.6-6.9-1.9l-0.5-0.3-5.1 1.3 1.4-4.9-0.3-0.5C3.3 20.4 2.6 18.2 2.6 16 2.6 8.6 8.6 2.6 16 2.6c3.6 0 7 1.4 9.5 3.9s3.9 5.9 3.9 9.5c0 7.4-6 13.4-13.4 13.4zm7.4-10.1c-0.4-0.2-2.4-1.2-2.8-1.3-0.4-0.1-0.6-0.2-0.9 0.2-0.3 0.4-1 1.3-1.3 1.5-0.2 0.3-0.5 0.3-0.9 0.1-0.4-0.2-1.7-0.6-3.3-2-1.2-1.1-2-2.4-2.3-2.8-0.2-0.4 0-0.6 0.2-0.8 0.2-0.2 0.4-0.5 0.6-0.7 0.2-0.2 0.3-0.4 0.4-0.7 0.1-0.3 0.1-0.5 0-0.7-0.1-0.2-0.9-2.2-1.2-3-0.3-0.8-0.7-0.7-0.9-0.7-0.2 0-0.5 0-0.7 0-0.3 0-0.7 0.1-1 0.5-0.3 0.4-1.3 1.3-1.3 3.2 0 1.9 1.4 3.7 1.5 3.9 0.2 0.3 2.7 4.1 6.5 5.8 0.9 0.4 1.6 0.6 2.2 0.8 0.9 0.3 1.8 0.2 2.4 0.2 0.7-0.1 2.2-0.9 2.5-1.8 0.3-0.9 0.3-1.6 0.2-1.8-0.1-0.2-0.4-0.3-0.7-0.5z"/>
        </svg>
        <!-- Instagram: camera frame (logo oficial) -->
        <svg v-else-if="channelCode === 'ig'" class="wa-ch-svg" viewBox="0 0 24 24">
          <path fill="#fff" d="M7.5 2h9C19.985 2 22 4.015 22 6.5v11c0 2.485-2.015 4.5-4.5 4.5h-9C5.015 22 3 19.985 3 17.5v-11C3 4.015 5.015 2 7.5 2zM7 4C5.343 4 4 5.343 4 7v10c0 1.657 1.343 3 3 3h10c1.657 0 3-1.343 3-3V7c0-1.657-1.343-3-3-3H7zm10.5 1.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/>
        </svg>
        <!-- Messenger (Facebook Page): lightning bubble -->
        <svg v-else-if="channelCode === 'fb'" class="wa-ch-svg" viewBox="0 0 24 24">
          <path fill="#fff" d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.44 3.14 7.18.16.14.26.34.27.55l.05 1.78a.8.8 0 0 0 1.12.71l1.99-.88a.8.8 0 0 1 .54-.04c.91.25 1.88.39 2.89.39 5.64 0 10-4.13 10-9.69C22 6.13 17.64 2 12 2zm6.01 7.4l-2.94 4.66a1.5 1.5 0 0 1-2.16.4l-2.34-1.75a.6.6 0 0 0-.72 0l-3.16 2.39c-.42.32-.97-.18-.7-.62l2.94-4.66a1.5 1.5 0 0 1 2.16-.4l2.34 1.75c.21.16.51.16.72 0l3.16-2.4c.42-.31.97.19.7.63z"/>
        </svg>
        <!-- TikTok: music note + shadow signature (cyan/red filter) -->
        <svg v-else-if="channelCode === 'tk'" class="wa-ch-svg wa-ch-svg-tk" viewBox="0 0 24 24">
          <path fill="#fff" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.86a8.16 8.16 0 0 0 4.77 1.52V6.94a4.85 4.85 0 0 1-1.84-.25z"/>
        </svg>
        <!-- Fallback: emoji para canales sin SVG (email, sms, tg, line, web) -->
        <span v-else class="wa-ch-fallback">{{ channelIcon }}</span>
      </span>
    </div>

    <div class="wa-body">
      <div class="wa-row1">
        <div class="wa-name" :class="{ 'wa-name-unread': hasUnread }">
          <svg
            v-if="isPinnedConversation"
            class="wa-pin-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M16 3l5 5-1.5 1.5-1-1-4 4 .5 4.5L13 19l-3-3-4 4-1-1 4-4-3-3 1.5-1.5L12 11l4-4-1-1L16 3z" />
          </svg>
          <svg
            v-else-if="isUserPinned"
            class="wa-pin-icon wa-userpin-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M16 3l5 5-1.5 1.5-1-1-4 4 .5 4.5L13 19l-3-3-4 4-1-1 4-4-3-3 1.5-1.5L12 11l4-4-1-1L16 3z" />
          </svg>
          {{ isPinnedConversation ? pinnedName : (currentContact.name || `Lead #${chat.id}`) }}
          <span
            v-if="isOfficialGroup"
            class="ml-1.5 px-1.5 py-px rounded-full text-[9px] font-bold tracking-wide align-middle bg-amber-500/15 text-amber-400"
            >OFICIAL</span
          >
          <span
            v-if="showBranchTag"
            class="ml-1.5 px-1.5 py-px rounded-md text-[9px] font-bold tracking-wide align-middle border"
            :class="branchTagClass"
            >{{ branchShort }}</span
          >
          <span v-if="chat._collapsedCount > 1" class="wa-numbadges" title="Escribió a estos números de la empresa">
            <span
              v-for="n in chat._collapsedNumbers"
              :key="n"
              class="wa-numbadge"
            >…{{ n }}</span>
          </span>
        </div>
        <div class="wa-time" :title="waitTooltip">
          <span class="wa-wait" :class="`wa-wait-${waitLevel}`">{{ waitLabel }}</span>
        </div>
      </div>
      <div class="wa-row2">
        <div class="wa-preview" :class="{ 'wa-preview-unread': hasUnread }">
          <VoiceCallStatus
            v-if="voiceCallData.status"
            :status="voiceCallData.status"
            :direction="voiceCallData.direction"
            :message-preview-class="''"
          />
          <MessagePreview
            v-else-if="lastMessageInChat"
            :message="lastMessageInChat"
            class="wa-preview-text"
          />
          <span v-else class="wa-preview-empty">
            {{ $t('CHAT_LIST.NO_MESSAGES') }}
          </span>
        </div>
        <div
          v-if="presenceChip"
          class="wa-presence"
          :class="`wa-presence-${presenceChip.kind}`"
          :title="
            presenceChip.kind === 'free'
              ? 'Nadie está atendiendo este chat'
              : `Atiende: ${presenceChip.label}`
          "
        >
          <span
            v-if="presenceChip.kind === 'free'"
            class="wa-presence-free-dot"
          />
          <span v-else-if="presenceChip.kind === 'me'" class="wa-presence-dot" />
          <span v-else class="wa-presence-mini">{{ presenceChip.initial }}</span>
          <span class="wa-presence-label">{{ presenceChip.label }}</span>
        </div>
        <!-- Globo "💬 N" de no leídos (variante B · Victor 2026-05-25): mismo
             lenguaje visual que la tarjeta del lead. El texto azul del mensaje
             se mantiene; el globo añade la cantidad y desaparece al leer. -->
        <span v-if="hasUnread" class="wa-unread-globe">💬 {{ unreadCount }}</span>
      </div>
      <div v-if="isPinnedConversation" class="wa-pinned-warning">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2 3 6v6c0 5 3.5 8.5 9 10 5.5-1.5 9-5 9-10V6l-9-4zm0 6a2 2 0 0 1 2 2c0 .7-.4 1.3-1 1.7V14h-2v-2.3c-.6-.4-1-1-1-1.7a2 2 0 0 1 2-2z" />
        </svg>
        <span>{{ pinnedWarning }}</span>
      </div>
    </div>

    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :conversation-labels="chat.labels"
        :conversation-url="conversationPath"
        :allowed-options="allowedContextMenuOptions"
        :is-pinned="isUserPinned"
        @toggle-pin="toggleUserPin"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @remove-label="onRemoveLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @delete-conversation="deleteConversation"
        @open-task-modal="openTaskModal"
        @close="closeContextMenu"
      />
    </ContextMenu>

    <NewTaskModal
      v-if="showTaskModal"
      :prefill-conversation-id="chat?.id"
      :prefill-contact-name="chat?.meta?.sender?.name"
      :prefill-contact-id="chat?.meta?.sender?.id"
      @close="closeTaskModal"
      @created="closeTaskModal"
    />

      <!-- PC/laptop: botón Fijar al pasar el mouse -->
      <button
        v-if="enableContextMenu"
        type="button"
        class="wa-pin-hover"
        :class="{ 'is-pinned': isUserPinned }"
        :title="isUserPinned ? 'Desfijar chat' : 'Fijar chat'"
        @click.stop="toggleUserPin"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 3l5 5-1.5 1.5-1-1-4 4 .5 4.5L13 19l-3-3-4 4-1-1 4-4-3-3 1.5-1.5L12 11l4-4-1-1L16 3z" />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* === Fijar chat · deslizar (celular) + botón hover (PC) · Victor 2026-06-02 === */
.wa-swipe-wrap {
  position: relative;
  overflow: hidden;
}
/* Acción "Fijar/Desfijar" revelada al deslizar → (queda debajo, a la izquierda) */
.wa-swipe-fijar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 84px;
  border: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: linear-gradient(180deg, #ecd49a, #c9a35a);
  color: #241d06;
  font-size: 11px;
  font-weight: 700;
  z-index: 0;
}
.wa-swipe-fijar svg {
  width: 18px;
  height: 18px;
}
/* La fila tapa la acción salvo la franja revelada (fondo opaco solo al deslizar) */
.wa-row.wa-swiping {
  position: relative;
  z-index: 1;
  background: #0c0e12;
}
.wa-row.wa-swipe-animate {
  transition: transform 0.18s ease, background 0.12s ease;
}
/* Botón Fijar al pasar el mouse (PC/laptop) — oculto en táctil (sin hover) */
.wa-pin-hover {
  position: absolute;
  top: 8px;
  right: 10px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.55);
  opacity: 0;
  cursor: pointer;
  transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;
  z-index: 3;
}
.wa-pin-hover svg {
  width: 14px;
  height: 14px;
}
.wa-row:hover .wa-pin-hover {
  opacity: 1;
}
.wa-pin-hover:hover {
  background: rgba(201, 163, 90, 0.18);
  color: #ecd49a;
}
.wa-pin-hover.is-pinned {
  opacity: 1;
  color: #c9a35a;
}
/* Indicador 📌 del chat fijado por el usuario (dorado) */
.wa-userpin-icon {
  color: #c9a35a;
}
.wa-row.wa-userpinned:not(.wa-active):not(.wa-selected):not(:hover) {
  background: linear-gradient(90deg, rgba(201, 163, 90, 0.05), transparent 60%);
}

/* === WA · ChatList row · WhatsApp Web clean (2026-05-13) === */
.wa-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  /* Altura FIJA e idéntica en todas las tarjetas (Victor 2026-06-10): el
     virtualizador (virtua) posiciona cada fila según la altura que mide; si
     unas miden distinto (p.ej. la tarjeta con aviso "Prohibido compartir"
     llevaba 3ª línea) la caché de alturas se desfasa al abrir acordeones o al
     reordenarse en vivo y las filas se ENCIMABAN. Altura constante = imposible
     que se solapen. */
  height: 64px;
  box-sizing: border-box;
  overflow: hidden;
  cursor: pointer;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  transition: background 0.12s ease;
  user-select: none;
}
.wa-row:hover {
  background: rgba(251,191,36,0.04);
}
/* === Seleccionado (Victor 2026-05-20): SOLO en el chat abierto ahora.
       Barrita dorada 3px + velo dorado sutil + nombre blanco bold. === */
.wa-row.wa-active,
.wa-row.wa-selected {
  background: linear-gradient(
    90deg,
    rgba(251,191,36,0.10),
    rgba(251,191,36,0.02) 70%,
    transparent
  );
}
.wa-row.wa-active::before,
.wa-row.wa-selected::before {
  content: '';
  position: absolute;
  left: 0; top: 8px; bottom: 8px;
  width: 3px;
  border-radius: 0 2px 2px 0;
  background: #fbbf24;
  box-shadow: 0 0 10px rgba(251,191,36,0.45);
  z-index: 2;
}
.wa-row.wa-active .wa-name,
.wa-row.wa-selected .wa-name {
  color: #fff;
  font-weight: 700;
}

/* === Fijado · número personal de Victor (2026-05-22) · acento dorado === */
.wa-row.wa-pinned {
  background: linear-gradient(
    90deg,
    rgba(216,177,90,0.14),
    rgba(216,177,90,0.03) 70%,
    transparent
  );
}
.wa-row.wa-pinned::after {
  content: '';
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 3px;
  background: #d8b15a;
  box-shadow: 0 0 10px rgba(216,177,90,0.45);
  z-index: 2;
}
.wa-row.wa-pinned .wa-name {
  color: #fff;
  font-weight: 700;
}
.wa-pin-icon {
  width: 13px;
  height: 13px;
  margin-right: 5px;
  color: #d8b15a;
  vertical-align: -1px;
}
/* Tarjeta "Victor Guzman Personal": el aviso ocupa la 2ª línea EN LUGAR del
   preview, así mide lo mismo que cualquier otra tarjeta (Victor 2026-06-10). */
.wa-row.wa-pinned .wa-row2 {
  display: none;
}
.wa-pinned-warning {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  font-size: 10.5px;
  font-weight: 500;
  line-height: 1.3;
  color: #e0a955;
  min-width: 0;
}
.wa-pinned-warning span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.wa-pinned-warning svg {
  width: 11px;
  height: 11px;
  flex-shrink: 0;
}

/* Vars de color por canal (se conservan para el badge en la esquina del
   avatar; ya no se usan para la barra ni el time de unread). */
.wa-row.wa-ch-wa    { --wa-ch-color: #25d366; }
.wa-row.wa-ch-ig    { --wa-ch-color: #e1306c; }
.wa-row.wa-ch-fb    { --wa-ch-color: #1877F2; }
.wa-row.wa-ch-tk    { --wa-ch-color: #ff0050; }
.wa-row.wa-ch-tw    { --wa-ch-color: #1da1f2; }
.wa-row.wa-ch-email { --wa-ch-color: #94a3b8; }
.wa-row.wa-ch-sms   { --wa-ch-color: #14b8a6; }
.wa-row.wa-ch-tg    { --wa-ch-color: #229ED9; }
.wa-row.wa-ch-line  { --wa-ch-color: #06c755; }
.wa-row.wa-ch-web   { --wa-ch-color: #64748b; }

/* === Channel icon badge in avatar corner (B1+iOS style) === */
.wa-ch-badge {
  position: absolute;
  bottom: -3px; right: -3px;
  width: 22px; height: 22px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: 2.5px solid #0a0a0a;
  background: var(--wa-ch-color, #64748b);
  pointer-events: none;
  overflow: hidden;
  z-index: 2;
}
.wa-ch-svg {
  width: 16px;
  height: 16px;
  display: block;
}
.wa-ch-svg-tk {
  filter: drop-shadow(1px 0 0 #25F4EE) drop-shadow(-1px 0 0 #FE2C55);
}
.wa-ch-fallback {
  font-size: 11px;
  font-weight: 800;
  color: #fff;
  line-height: 1;
}

/* Backgrounds oficiales por canal */
.wa-ch-badge-wa {
  background: linear-gradient(180deg, #4FCE5D, #29A71A);
}
.wa-ch-badge-ig {
  background: radial-gradient(circle at 30% 110%, #fdf497, #fd5949 45%, #d6249f 60%, #285aeb 90%);
}
.wa-ch-badge-fb {
  background: #0084FF;
}
.wa-ch-badge-tk {
  background: #000;
}
.wa-ch-badge-tw {
  background: #000;
}
.wa-ch-badge-email {
  background: #475569;
}
.wa-ch-badge-sms {
  background: #14b8a6;
}
.wa-ch-badge-tg {
  background: #229ED9;
}
.wa-ch-badge-line {
  background: #06c755;
}
.wa-ch-badge-web {
  background: #64748b;
}

/* Avatar */
.wa-avatar-wrap {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  position: relative;
}
.wa-checkbox-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.55);
  border-radius: 50%;
  cursor: pointer;
}

/* Body */
.wa-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.wa-row1 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.wa-row2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.wa-name {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255,255,255,0.92);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}
.wa-name-unread {
  font-weight: 700;
  color: #fff;
}
/* Badges de números (tarjeta colapsada multi-número · Victor 2026-05-23) */
.wa-numbadges {
  display: inline-flex;
  gap: 4px;
  margin-left: 6px;
  vertical-align: middle;
}
.wa-numbadge {
  font-size: 9px;
  font-weight: 800;
  padding: 1px 6px;
  border-radius: 999px;
  color: #c9a35a;
  background: rgba(201, 163, 90, 0.12);
  border: 1px solid rgba(201, 163, 90, 0.4);
}
.wa-numbadge:first-child {
  color: #5b9bff;
  background: rgba(91, 155, 255, 0.12);
  border-color: rgba(91, 155, 255, 0.4);
}
.wa-numbadge:nth-child(2) {
  color: #34d399;
  background: rgba(52, 211, 153, 0.12);
  border-color: rgba(52, 211, 153, 0.4);
}

.wa-time {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  flex-shrink: 0;
}
/* Duración con color de urgencia (Victor 2026-05-20 · T4):
   verde <30min · gris <24h · ámbar <7d · rojo >7d. */
.wa-wait {
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
}
.wa-wait-ok { color: #22c55e; }
.wa-wait-neutral { color: rgba(255,255,255,0.5); }
.wa-wait-warn { color: #fbbf24; }
.wa-wait-danger { color: #f87171; }

.wa-preview {
  font-size: 12.5px;
  color: rgba(255,255,255,0.55);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 0 1 auto;
  max-width: 520px;
  min-width: 0;
  line-height: 1.35;
}
.wa-preview-unread {
  /* Mensaje del cliente que escribió (Victor 2026-05-20 · variante A4):
     azul vivo + bold. Más pequeño (10.5px) y truncado a ~60% del ancho
     de la bandeja con "…" si es largo (Victor 2026-05-20). */
  color: #7aa8ff;
  font-weight: 700;
  font-size: 10.5px;
  max-width: 60%;
}
.wa-preview-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.wa-preview-empty {
  font-style: italic;
  color: rgba(255,255,255,0.35);
}

/* Globo "💬 N" de no leídos (variante B · Victor 2026-05-25): cantidad de
   mensajes entrantes sin leer, mismo estilo que la tarjeta del lead. */
.wa-unread-globe {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: #2f80ed;
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  box-shadow: 0 0 9px rgba(74,158,255,0.55);
}

/* Vista compartida · chip "quién atiende / Libre / Tú" (2026-05-18) */
.wa-presence {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 600;
  padding: 2px 8px 2px 5px;
  border-radius: 999px;
  flex-shrink: 0;
  max-width: 116px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.05);
  color: #8c8c92;
}
.wa-presence-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.wa-presence-me {
  background: rgba(57, 217, 138, 0.14);
  border-color: rgba(57, 217, 138, 0.30);
  color: #39d98a;
}
.wa-presence-busy {
  background: rgba(224, 163, 65, 0.12);
  border-color: rgba(224, 163, 65, 0.30);
  color: #e0a341;
}
.wa-presence-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #39d98a;
  box-shadow: 0 0 6px #39d98a;
  flex-shrink: 0;
}
.wa-presence-free-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px dashed rgba(255, 255, 255, 0.22);
  flex-shrink: 0;
}
.wa-presence-mini {
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1f6f4a, #2a8f60);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Tooltip preview de Avatar y deep selectors heredados de chatwoot */
:deep(.user-thumbnail-box) {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
